import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useGetCap, useGetMunicipalities } from "src/networking/terminals";
import { atomProvinces } from "src/storage";
import {
  CompanyType,
  Gender,
  InputSelectOption,
  ContractorType,
  PeriodType,
  ViarioNationCode,
  Nationality,
  YesNoAnswersNew,
} from "src/types";

/**
 * Hook that provides translated options for the company type select input.
 */
export const useCompanyTypeOptions = () => {
  const { t } = useTranslation();
  const companyTypeOptions: InputSelectOption<CompanyType>[] = [
    { id: CompanyType.ODV, value: t("data_entry_step_2.company_type_odv") },
    { id: CompanyType.APS, value: t("data_entry_step_2.company_type_aps") },
    { id: CompanyType.ASD, value: t("data_entry_step_2.company_type_asd") },
    { id: CompanyType.SSD, value: t("data_entry_step_2.company_type_ssd") },
    { id: CompanyType.REL, value: t("data_entry_step_2.company_type_rel") },
    { id: CompanyType.FOU, value: t("data_entry_step_2.company_type_fou") },
    { id: CompanyType.COO, value: t("data_entry_step_2.company_type_coo") },
    { id: CompanyType.SOC, value: t("data_entry_step_2.company_type_soc") },
    { id: CompanyType.ALT, value: t("data_entry_step_2.company_type_oth") },
  ];

  return { companyTypeOptions };
};

/**
 * Hook that provides translated options for the coverage period selection.
 */
export const usePeriodOptions = () => {
  const { t } = useTranslation();
  const periodOptions: InputSelectOption<PeriodType>[] = [
    { id: PeriodType.DAILY, value: t("data_entry_step_1.coverage_daily") },
    { id: PeriodType.PERIOD, value: t("data_entry_step_1.coverage_period") },
  ];

  return { periodOptions };
};

/**
 * Hook that provides translated options for the insured type.
 */
export const useContractorTypeOptions = () => {
  const { t } = useTranslation();
  const contractorTypeOptions: InputSelectOption<ContractorType>[] = [
    {
      id: ContractorType.LEGAL,
      value: t("data_entry_step_2.contractor_legal"),
    },
    {
      id: ContractorType.NATURAL,
      value: t("data_entry_step_2.contractor_natural"),
    },
  ];

  return { contractorTypeOptions };
};

/**
 * Hook that provides translated options for nationality selection.
 */
export const useNationalityOptions = () => {
  const { t } = useTranslation();
  const nationalityOptions: InputSelectOption<Nationality>[] = [
    { id: Nationality.IT, value: t("commons.yes_label") },
    { id: Nationality.EXT, value: t("commons.no_label") },
  ];

  return { nationalityOptions };
};

/**
 * Hook that provides translated options for a yes/no question related to FC.
 */
export const useFcQuestionOptions = () => {
  const { t } = useTranslation();
  const fcQuestionOptions: InputSelectOption<YesNoAnswersNew>[] = [
    { id: YesNoAnswersNew.YES, value: t("commons.yes_label") },
    { id: YesNoAnswersNew.NO, value: t("commons.no_label") },
  ];

  return { fcQuestionOptions };
};

/**
 * Hook that provides translated options for gender selection.
 */
export const useGenderOptions = () => {
  const { t } = useTranslation();
  const genderOptions: InputSelectOption<Gender>[] = [
    { id: Gender.M, value: t("data_entry_step_2.insured_gender_male") },
    { id: Gender.F, value: t("data_entry_step_2.insured_gender_female") },
  ];

  return { genderOptions };
};

/**
 * Hook that provides translated options for contractor insured questions.
 */
export const useContractorIsInsuredQuestionId = () => {
  const { t } = useTranslation();
  const contractorIsInsuredQuestionOptions: InputSelectOption<YesNoAnswersNew>[] =
    [
      { id: YesNoAnswersNew.YES, value: t("commons.yes_label") },
      { id: YesNoAnswersNew.NO, value: t("commons.no_label") },
    ];

  return { contractorIsInsuredQuestionOptions };
};

/**
 * Hook that provides options for provinces.
 */
export const useProvincesOptions = () => {
  const provinces = useRecoilValue(atomProvinces);
  const provincesOptions: InputSelectOption<string>[] = provinces.map(
    (province) => ({
      id: province.codice,
      value: province.descrizione,
    })
  );

  return { provincesOptions };
};

/**
 * Hook that provides options for municipalities based on provinceCode passed.
 */
export const useMunicipalitiesOptions = (provinceCode?: string) => {
  const { crossGate: getMunicipalities } = useGetMunicipalities();
  const [municipalitiesOptions, setMunicipalitiesOptions] = useState<
    InputSelectOption<string>[]
  >([]);

  useEffect(() => {
    if (!provinceCode) {
      if (municipalitiesOptions.length > 0) setMunicipalitiesOptions([]);
      return;
    }

    getMunicipalities(
      {
        queryParams: {
          codiceNazione: ViarioNationCode.ITALY,
          codiceProvincia: provinceCode,
        },
      },
      {
        onSuccess(municipalities) {
          const municipalitiesOptions = municipalities.map((municipality) => ({
            id: municipality.codice,
            value: municipality.descrizione,
          }));

          setMunicipalitiesOptions(municipalitiesOptions);
        },
      }
    );
  }, [provinceCode]);

  return { municipalitiesOptions };
};

/**
 * Hook that provides option for cap based on provinceCode and municipalityCode passed.
 */
export const useCapOptions = (
  provinceCode?: string,
  municipalityCode?: string
) => {
  const { crossGate: getCap } = useGetCap();
  const [capOptions, setCapOptions] = useState<InputSelectOption<string>[]>([]);

  useEffect(() => {
    if (!provinceCode || !municipalityCode) {
      if (capOptions.length > 0) setCapOptions([]);
      return;
    }

    getCap(
      {
        queryParams: {
          codiceNazione: ViarioNationCode.ITALY,
          codiceProvincia: provinceCode,
          codiceComune: municipalityCode,
        },
      },
      {
        onSuccess(capArray) {
          const capOptions = capArray.map((cap) => ({
            id: cap.descrizione, //I cap hanno tutti lo stesso codice (in questo caso usa la descrizione per differenziare l'id opzione)
            value: cap.descrizione,
          }));

          setCapOptions(capOptions);
        },
      }
    );
  }, [provinceCode, municipalityCode]);

  return { capOptions };
};
